import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import WaveBg from "@/images/wave-bg.svg";

const parse = (str) => {
    const m = (str || "").match(/^[\d.,\s]*/);
    const num = parseInt((m?.[0] || "").replace(/[.,\s]/g, ""), 10) || 0;
    return { num, suffix: (str || "").slice((m?.[0] || "").length).trim() };
};

const Statistics = () => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    const [counts, setCounts] = useState([0, 0, 0, 0]);

    const stats = useMemo(() => [
        { ...parse(t("home-page.statistics-stat-1-number")), description: t("home-page.statistics-stat-1-description") },
        { ...parse(t("home-page.statistics-stat-2-number")), description: t("home-page.statistics-stat-2-description") },
        { ...parse(t("home-page.statistics-stat-3-number")), description: t("home-page.statistics-stat-3-description") },
        { ...parse(t("home-page.statistics-stat-4-number")), description: t("home-page.statistics-stat-4-description") },
    ], [t]);

    useEffect(() => {
        if (!ref.current) return;
        const obs = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold: 0.2 });
        obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        if (!inView) return;
        const targets = stats.map((s) => s.num);
        const start = Date.now();
        const id = setInterval(() => {
            const p = Math.min((Date.now() - start) / 2000, 1);
            const ease = 1 - (1 - p) ** 4;
            setCounts(targets.map((n) => Math.floor(ease * n)));
            if (p >= 1) clearInterval(id);
        }, 16);
        return () => clearInterval(id);
    }, [inView, stats]);

    return (
        <section
            ref={ref}
            className="relative pt-[60px] pb-[60px] md:pt-[100px] md:pb-[150px] bg-gradient-to-b from-[#091F59] to-[#1A3884] md:rounded-[64px] mb-[100px] md:mb-[200px] rounded-[24px] mx-[8px] overflow-hidden"
        >
            <div className="absolute inset-0">
                <Image
                    src={WaveBg}
                    alt="Wave background"
                    fill
                    className="object-cover"
                    priority
                />
            </div>
            <div className="relative max-w-[90%] mx-auto z-10">
                <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-12 mb-[32px] md:mb-[64px]">
                    <div className="flex xs:flex-row flex-col items-center gap-4">
                        <h2 className="text-[24px] md:text-[48px] lg:text-[64px] font-[500] text-white leading-tight">
                            {t("home-page.statistics-title-1")}
                        </h2>
                        <h2 className="text-[24px] md:text-[48px] lg:text-[64px] font-[500] text-[#FF4081] leading-tight">
                            {t("home-page.statistics-title-2")}
                        </h2>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row items-center justify-center relative">
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className="flex flex-col items-center justify-center w-full md:w-auto relative"
                            style={{
                                zIndex: stats.length - index,
                            }}
                        >
                            <div 
                                className={`w-[190px] h-[190px] lg:w-[260px] lg:h-[260px] xl:w-[290px] xl:h-[290px] rounded-full border border-white/40 flex flex-col items-center justify-center bg-transparent ${
                                    index > 0 ? '-mt-[30px] md:mt-0 md:-ml-[15px] lg:-ml-[25px]' : ''
                                }`}
                            >
                                <div className="text-[20px] lg:text-[32px] font-[500] text-white mb-3 md:mb-4">
                                    {(counts[index] ?? 0).toLocaleString()}{stat.suffix}
                                </div>
                                <p className="text-white text-[12px] lg:text-[16px] font-[400] text-center leading-relaxed px-5">
                                    {stat.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Statistics;
